// Taking a child through a list of English questions, one at a time.
//
// Extracted from EngDiagAttemptPlayer so the Skill Builder player can reuse it. This is the part
// that is GENUINELY the same between a diagnostic and a Skill Builder - ask the server what is
// due, show it, send the answer, ask again - and it carries five fixes that were each found the
// hard way:
//
//   1. `timeAllowed || null`, because a stored 0 means "no limit" and `?? null` TIMEOUT-skipped
//      a question the instant it appeared, twice, retiring it before the child could read it.
//   2. The countdown is cleared BEFORE the timeout skip fires, or a failed skip re-fires it every
//      pass and POSTs a skip row each time.
//   3. advance() checks for an error payload, or a failed fetch was stored as state and the child
//      was shown the finish card for a test that still owed questions.
//   4. resync() after a failure that happened AFTER the server accepted a write, so a retry
//      cannot write a second attempt row or spend another of the question's skips.
//   5. An in-flight guard, so a double click submits once.
//
// A second copy of those would drift from this one, and the drift would be invisible until a
// child lost an answer. What differs between the two activities - the chrome, the progress
// wording, whether a verdict is shown - stays in the players.

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type {
  EngDiagQuestion,
  EngDiagSkipReason,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"
import { answerProgress } from "../questionParts"

/**
 * Types with nothing for the student to answer: they read aloud, or just look at something. The
 * tutor marks these afterwards, so the submit guard must not strand a child on one.
 * Mirrors NON_GRADED_TYPES in tutor-backend modules/eng-diag/answerGrading.js.
 */
export const NOTHING_TO_ANSWER = [
  "read_words",
  "display_content",
  "phoneme_segmentation",
  "flashcard",
]

/**
 * Whether there is anything worth submitting.
 *
 * ⚠️ EVERY part, not any part - reversed on the PM's instruction (2026-10-26) after a matching
 * question was handed in with one of its two pairs placed. The marker counts an unfilled part as
 * wrong, so submitting early cost a mark that was there to be had.
 *
 * The cost of the reversal is real and was the reason for the old rule: a child who can do two of
 * three blanks can no longer hand that in, and is pushed towards guessing or skipping. "Skip for
 * now" is still the honest way out, and the player names how many parts are left so the button is
 * never silently grey.
 *
 * The counting lives in questionParts.ts, next to the note about keeping it in step with
 * gradeTargets on the server.
 */
export function hasAnswer(
  question: { id?: string; type?: string; options?: any } | null | undefined,
  answers: EngDiagStudentAnswers,
): boolean {
  return answerProgress(question, answers).complete
}

/** How far through the child is. Both activities report this, under different column names. */
export type EngAttemptProgress = {
  answered: number
  total: number
}

/** What the server says after an answer or a skip. */
export type EngAttemptResult = {
  status: 0 | 1
  error?: string
  session_complete?: boolean
  next_question_key?: string | null
  /** Only a Skill Builder returns a verdict; a diagnostic deliberately does not. */
  is_correct?: boolean | null
  awaiting_marking?: boolean
}

/**
 * The server calls this runner makes. Each activity supplies its own, which is what keeps the
 * endpoint names and payload field names out of here.
 *
 * `state` is returned whole as well as normalised, so a player can read its own extras - the
 * diagnostic's topic blocks, the Skill Builder's skill name - without this file knowing about
 * either.
 */
export type EngAttemptTransport<TState> = {
  getState: (signal?: AbortSignal) => Promise<TState & { error?: string }>
  /** Where the child is, in the two numbers every player needs. */
  readProgress: (state: TState) => EngAttemptProgress
  /** The question key the state says is due, or null when there is none. */
  readCurrentKey: (state: TState) => string | null
  getQuestion: (
    questionKey: string,
    signal?: AbortSignal,
  ) => Promise<{ question: EngDiagQuestion | null; error?: string }>
  submitAnswer: (args: {
    questionKey: string
    response: EngDiagStudentAnswers
    durationSeconds: number
  }) => Promise<EngAttemptResult>
  skipQuestion: (args: {
    questionKey: string
    reason: EngDiagSkipReason
    durationSeconds: number
  }) => Promise<EngAttemptResult>
}

export type EngAttemptCopy = {
  ERROR_LOAD: string
  ERROR_SUBMIT: string
  ERROR_SKIP: string
  ERROR_CONNECTION: string
}

/**
 * What to put on the screen for a failure.
 *
 * The throws in this file carry a message the SERVER wrote, which is meant to be read and says
 * something useful ("Question not found"). An axios failure carries "Network Error" or "timeout
 * of 30000ms exceeded", which says nothing to a child and reads as a crash. Those get the
 * connection line and the action that goes with it.
 */
function messageFor(error: any, copy: EngAttemptCopy, fallback: string): string {
  if (error?.isAxiosError || error?.code === "ECONNABORTED" || error?.code === "ERR_NETWORK") {
    return copy.ERROR_CONNECTION
  }
  return error?.message || fallback
}

type Options<TState> = {
  transport: EngAttemptTransport<TState>
  /**
   * MUST BE STABLE too, for the same reason as `transport`: the load effect depends on it,
   * so an object built inline would refetch the activity on every render. Both players pass
   * a frozen module constant.
   */
  copy: EngAttemptCopy
  /**
   * Called once there is nothing left to serve, so the page can show its finish screen.
   *
   * MUST BE STABLE across renders - a setState function, or something memoised. It feeds the
   * load() callback, which the first effect depends on, so an inline arrow makes that effect
   * re-run every render and the activity refetches in a loop. Same for `transport`.
   */
  onFinished?: (state: TState) => void
  /**
   * Hold the question on screen after submitting, instead of moving straight on.
   *
   * A Skill Builder shows the child whether they got it right, which needs a beat between the
   * answer landing and the next question appearing. A diagnostic has nothing to show, so it
   * advances immediately. The next question is already known either way - the server returned
   * its key - so continueAfterFeedback() is a render, not another round trip.
   */
  holdFeedback?: boolean
}

export function useEngAttemptRunner<TState>({
  transport,
  copy,
  onFinished,
  holdFeedback = false,
}: Options<TState>) {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = useState<TState | null>(null)
  const [question, setQuestion] = useState<EngDiagQuestion | null>(null)
  const [answers, setAnswers] = useState<EngDiagStudentAnswers>({})
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  /** The verdict being shown, when feedback is held. Null means nothing is being shown. */
  const [feedback, setFeedback] = useState<EngAttemptResult | null>(null)

  const canSubmit = hasAnswer(question, answers)
  /**
   * How much of the question is done, for the player to show. Exposed rather than recomputed
   * there, so the message and the button can never disagree about what is still missing.
   */
  const answerParts = answerProgress(question, answers)

  /** When the current question was first shown, for duration_seconds. */
  const shownAt = useRef<number>(Date.now())
  /** Guards against a double click submitting the same answer twice. */
  const inFlight = useRef(false)

  const elapsed = () => Math.max(0, Math.round((Date.now() - shownAt.current) / 1000))

  const showQuestion = useCallback((next: EngDiagQuestion | null) => {
    setQuestion(next)
    setAnswers({})
    setFeedback(null)
    // `|| null`, not `?? null`: a timeAllowed of 0 has to mean "no limit". LCMS stores
    // time_allowed as min(0), so 0 is a value an author or a bulk upload can write, and the
    // expiry branch below is `secondsLeft <= 0` - with `??` the question was TIMEOUT-skipped the
    // instant it appeared, twice, and then retired by MAX_SKIPS_PER_QUESTION without the student
    // ever reading it.
    setSecondsLeft(next?.timeAllowed || null)
    shownAt.current = Date.now()
  }, [])

  /** Load the activity and whatever question is due. Also how it resumes. */
  const load = useCallback(async (signal?: AbortSignal) => {
    const loaded = await transport.getState(signal)
    if (loaded.error) throw new Error(loaded.error)
    setState(loaded)

    const currentKey = transport.readCurrentKey(loaded)
    if (!currentKey) {
      showQuestion(null)
      onFinished?.(loaded)
      return
    }
    const served = await transport.getQuestion(currentKey, signal)
    if (served.error) throw new Error(served.error)
    showQuestion(served.question)
  }, [transport, showQuestion, onFinished])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        await load(controller.signal)
      } catch (e: any) {
        if (!controller.signal.aborted) setError(messageFor(e, copy, copy.ERROR_LOAD))
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [load, copy])

  /**
   * Move on after an answer or a skip. The server tells us the next key, so there is no local
   * list to keep in step.
   */
  const advance = useCallback(async (result: EngAttemptResult) => {
    const refreshed = await transport.getState()
    // Both error checks matter. Without them a failed fetch stored the error object as state and
    // then called showQuestion(undefined), which renders the "all done" card - so a student whose
    // request failed was shown the finish screen, with no error and without onFinished ever
    // firing, and walked away from an activity that still owed questions.
    if (refreshed.error) throw new Error(refreshed.error)
    setState(refreshed)

    if (result.session_complete || !result.next_question_key) {
      showQuestion(null)
      onFinished?.(refreshed)
      return
    }
    const served = await transport.getQuestion(result.next_question_key)
    if (served.error) throw new Error(served.error)
    showQuestion(served.question)
  }, [transport, showQuestion, onFinished])

  /**
   * Recover from a failure that happened AFTER the server accepted something.
   *
   * submit() and skip() only reach advance() once the server has returned status 1 - the attempt
   * or skip row is already written. If advance() then fails (the state fetch blips), the old
   * question is still on screen with Submit live, so a retry writes a SECOND attempt row for the
   * same question; submitAnswer has no idempotency guard, and a second skip counts towards
   * MAX_SKIPS_PER_QUESTION and can retire a question the student answered.
   *
   * Re-loading asks the server what is actually due now, which is the only safe answer after a
   * write we know landed. If that fails too the error already on screen stands.
   */
  const resync = useCallback(async () => {
    try {
      await load()
    } catch {
      // Keep the original error: it describes what the student should act on.
    }
  }, [load])

  /** The held verdict dismissed, and on to the question the server already named. */
  const continueAfterFeedback = useCallback(async () => {
    if (!feedback) return
    const held = feedback
    setFeedback(null)
    setBusy(true)
    try {
      await advance(held)
    } catch (e: any) {
      setError(messageFor(e, copy, copy.ERROR_SUBMIT))
      await resync()
    } finally {
      setBusy(false)
    }
  }, [feedback, advance, resync, copy])

  const submit = useCallback(async () => {
    if (!question?.sessionQuestionKey || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      const result = await transport.submitAnswer({
        questionKey: question.sessionQuestionKey,
        response: answers,
        durationSeconds: elapsed(),
      })
      if (result.status !== 1) throw new Error(result.error || copy.ERROR_SUBMIT)
      if (holdFeedback) {
        // Progress is refreshed now rather than on dismissal, so the counter under the verdict
        // already includes the answer the child just gave.
        const refreshed = await transport.getState()
        if (!refreshed.error) setState(refreshed)
        setFeedback(result)
      } else {
        await advance(result)
      }
    } catch (e: any) {
      setError(messageFor(e, copy, copy.ERROR_SUBMIT))
      // The answer MAY have been accepted - a timeout cannot tell us either way, since the
      // server has no idempotency guard and the request may have landed after we gave up.
      // Re-sync asks what is actually due now, so a retry cannot write a second attempt row.
      await resync()
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }, [transport, question, answers, advance, resync, holdFeedback, copy])

  const skip = useCallback(async (reason: EngDiagSkipReason) => {
    if (!question?.sessionQuestionKey || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    try {
      const result = await transport.skipQuestion({
        questionKey: question.sessionQuestionKey,
        reason,
        durationSeconds: elapsed(),
      })
      if (result.status !== 1) throw new Error(result.error || copy.ERROR_SKIP)
      // A skip is never held for feedback: there is no verdict on a question nobody answered.
      await advance(result)
    } catch (e: any) {
      setError(messageFor(e, copy, copy.ERROR_SKIP))
      // The skip was recorded; a retry would spend another of the question's limited skips.
      await resync()
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }, [transport, question, advance, resync, copy])

  /** Per-question countdown, for questions the author gave a time limit. */
  useEffect(() => {
    if (secondsLeft === null || busy) return
    if (secondsLeft <= 0) {
      // Stop the clock BEFORE skipping, not only in showQuestion afterwards. This effect re-runs
      // when `busy` clears, and if skip's advance() failed - a network blip, which is untimed and
      // unretried - secondsLeft would still be 0, so it fired skip again, and again, POSTing a
      // skip row every pass. Clearing it first means a failed timeout stops at one skip and the
      // error is shown; the next question sets its own limit.
      setSecondsLeft(null)
      void skip("TIMEOUT")
      return
    }
    const timer = setTimeout(() => setSecondsLeft(s => (s === null ? null : s - 1)), 1000)
    return () => clearTimeout(timer)
  }, [secondsLeft, busy, skip])

  const progress = useMemo(
    () => (state ? transport.readProgress(state) : { answered: 0, total: 0 }),
    [state, transport],
  )

  return {
    loading,
    busy,
    error,
    state,
    question,
    answers,
    setAnswers,
    canSubmit,
    answerParts,
    secondsLeft,
    progress,
    /** The verdict on screen, when the activity holds feedback. Null when nothing is shown. */
    feedback,
    submit,
    skip,
    continueAfterFeedback,
  }
}
